import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useSearchParams } from 'react-router-dom';
import { ProductCard } from '../components/ProductCard';
import { Icon } from '../ui/Icon';
import { Bouton, Feuille, Puce, Vide, cn } from '../ui/kit';
import { Fil } from './partages';
import {
  TRIS, eurSansCentimes, famille as trouverFamille, filtrer, groupesDeFiltres,
  produitsDe, rechercher, trier, type GroupeFacette, type Selection, type Tri
} from '../data/repository';
import { PRODUITS } from '../data/mock-data';

/**
 * EXPLORATION — le point n° 8 de la revue, traité à la racine, puis repris le
 * 18 octobre pour trois raisons :
 *
 *   · **deux dispositions de carte** — « grille » (portrait) et « liste »
 *     (paysage). Le composant `ProductCard` ne change pas d'une vue à l'autre :
 *     seuls les attributs et les classes du **conteneur** bougent, et la feuille
 *     reprend la carte en `[data-vue='liste']` (cf. `app.css`). Rien n'est
 *     démonté, rien n'est rechargé : la grille se réorganise sous les doigts ;
 *
 *   · **une barre de filtres horizontale** de 1024 à 1536 px. Sur un portable
 *     13,3" (1440 px de large), la colonne de 248 px volait au catalogue la
 *     largeur dont il a le plus besoin ; à partir de 1536 px (poste de bureau),
 *     la colonne revient.
 *     La bascule est une **frontière de largeur**, pas un choix utilisateur :
 *     le palier natif `2xl` (96rem = 1536 px, cf. `app.css`) ;
 *
 *   · **les filtres naissent des produits** — un groupe n'apparaît que s'il
 *     distingue au moins deux pièces ; aucune facette vide.
 */
export function Catalogue() {
  const { famille: slugFamille } = useParams<{ famille: string }>();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const familleActive = slugFamille ? trouverFamille(slugFamille) : undefined;

  /* La disposition vit dans l'adresse : elle se partage, se rafraîchit et se
     met en signet sans rien casser. Défaut : grille. */
  const vue: 'grille' | 'liste' = params.get('vue') === 'liste' ? 'liste' : 'grille';
  const changerVue = (v: 'grille' | 'liste') => {
    setParams(prev => {
      const suivant = new URLSearchParams(prev);
      if (v === 'liste') suivant.set('vue', 'liste'); else suivant.delete('vue');
      return suivant;
    }, { replace: true });
  };

  const base = useMemo(() => (slugFamille ? produitsDe(slugFamille) : PRODUITS), [slugFamille]);
  const groupes = useMemo(() => groupesDeFiltres(slugFamille ?? null), [slugFamille]);

  const [selection, setSelection] = useState<Selection>({});
  const [tri, setTri] = useState<Tri>('recommande');
  const [filtresOuverts, setFiltresOuverts] = useState(false);

  /* Un seul panneau de filtre ouvert à la fois — jamais deux couches empilées.
     Le panneau retient l'élément qui l'a ouvert : rendu dans un portail, il se
     place sur son rectangle et le suit (défilement du rail, rotation, zoom). */
  const [menu, setMenu] = useState<{ id: string; ancre: HTMLElement } | null>(null);
  const barreRef = useRef<HTMLDivElement>(null);

  /* ── La barre se colle, et se resserre ──────────────────────────────────
     `collee` bascule quand le haut naturel de la barre passe sous l'en-tête.
     `hRepos` fige la hauteur d'avant : la barre visible rétrécit, sa place dans
     le flux ne bouge pas. Sans ce gel, la grille remonterait de 80 px à chaque
     passage du point d'ancrage — un sursaut à chaque aller-retour.
     `hBarre`/`hEntete` servent à la colonne de gauche, qui doit se poser sous la
     barre réelle, quelle que soit sa hauteur du moment. */
  const [collee, setCollee] = useState(false);
  /* Au-delà de 1400 px, la barre n'a que le compte et le tri : la serrer
     davantage n'apporte rien. On garde donc la disposition et « tout effacer »
     même collée — ils disparaissent sur portable, où chaque pixel compte. */
  const [large, setLarge] = useState(false);
  const [hRepos, setHRepos] = useState(0);
  const [hBarre, setHBarre] = useState(0);
  const [hEntete, setHEntete] = useState(0);
  const zoneRef = useRef<HTMLDivElement>(null);
  const jalonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let frame = 0;
    const mesurer = () => {
      frame = 0;
      /* Passer de la barre à la colonne (ou l'inverse) pendant qu'un menu est
         ouvert laisserait un panneau flottant sans gâchette : on le referme. */
      const horsBarre = window.innerWidth >= 1400 || window.innerWidth < 1024;
      if (horsBarre) setMenu(m => (m ? null : m));
      setLarge(window.innerWidth >= 1400);
      const entete = document.querySelector('header.sticky');
      setHEntete(entete ? Math.round(entete.getBoundingClientRect().height) : 0);
      const zone = zoneRef.current;
      const barre = barreRef.current;
      if (!zone) return;
      /* On lit le `top` calculé de la barre plutôt que de le réécrire ici : la
         valeur vit dans les classes (`top-16`, `lg:top-[120px]`), et une
         constante recopiée en JavaScript finirait par mentir. */
      const seuil = parseFloat(getComputedStyle(zone).top) || 0;
      const colleeMaintenant = zone.getBoundingClientRect().top <= seuil + 0.5;
      if (barre) {
        const h = Math.round(barre.getBoundingClientRect().height);
        setHBarre(h);
        if (!colleeMaintenant) setHRepos(h);
      }
      setCollee(colleeMaintenant);
    };
    /* Un seul calcul par image : le navigateur émet des dizaines d'événements
       de défilement par seconde, `requestAnimationFrame` les fond en un. */
    const planifier = () => { if (!frame) frame = requestAnimationFrame(mesurer); };
    mesurer();
    window.addEventListener('scroll', planifier, { passive: true });
    window.addEventListener('resize', planifier);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', planifier);
      window.removeEventListener('resize', planifier);
    };
  }, []);

  useEffect(() => {
    if (!menu) return;
    const surClic = (e: MouseEvent) => {
      const cible = e.target as Element | null;
      /* Le panneau est dans un portail : il n'est plus contenu dans `barreRef`.
         On le reconnaît à son attribut, sinon le premier clic sur une case
         fermerait le panneau avant que la case ne soit cochée. */
      if (barreRef.current?.contains(cible as Node)) return;
      if (cible?.closest?.('[data-menu-filtre]')) return;
      setMenu(null);
    };
    const surTouche = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(null); };
    document.addEventListener('mousedown', surClic);
    document.addEventListener('keydown', surTouche);
    return () => {
      document.removeEventListener('mousedown', surClic);
      document.removeEventListener('keydown', surTouche);
    };
  }, [menu]);

  /* Changer de famille remet les filtres à zéro : garder « 28 jours » en arrivant
     dans la volaille donnerait une liste vide et un client perdu. */
  useEffect(() => { setSelection({}); setMenu(null); }, [slugFamille]);

  const resultats = useMemo(() => {
    let liste = base;
    if (q.trim().length >= 2) {
      const trouves = new Set(rechercher(q, 100).map(p => p.id));
      liste = liste.filter(p => trouves.has(p.id));
    }
    return trier(filtrer(liste, selection, groupes), tri);
  }, [base, q, selection, groupes, tri]);

  const nbActifs = Object.values(selection).reduce((n, l) => n + l.length, 0);
  const basculer = (groupeId: string, optionId: string) => {
    setSelection(prev => {
      const actuels = prev[groupeId] ?? [];
      const suivants = actuels.includes(optionId) ? actuels.filter(x => x !== optionId) : [...actuels, optionId];
      return { ...prev, [groupeId]: suivants };
    });
  };
  const toutEffacer = () => setSelection({});
  const comptePieces = `${resultats.length} pièce${resultats.length !== 1 ? 's' : ''}`;

  return (
    <main id="contenu" className="mx-auto max-w-[1280px] px-5 pb-20 pt-6 sm:px-6">
      <Fil fils={[
        { libelle: 'La boutique', vers: '/boutique' },
        ...(familleActive ? [{ libelle: familleActive.nom }] : []),
        ...(q ? [{ libelle: `Recherche « ${q} »` }] : []),
      ]} />

      {/* ---------------------------------------------------------- en-tête */}
      {familleActive ? (
        <header className="relative overflow-hidden rounded-[24px] bg-linen px-5 py-8 sm:px-8 sm:py-10">
          <div className="relative z-10 max-w-2xl">
            <p className="eyebrow">{familleActive.surtitre}</p>
            <h1 className="mt-2 text-[clamp(2rem,4.4vw,3.1rem)]">{familleActive.nom}</h1>
            <p className="mt-3 text-[0.9375rem] leading-relaxed text-ink-soft">{familleActive.texte}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted">
              <span className="inline-flex items-center gap-1.5"><Icon name="package" size={16} /> {`${familleActive.nombre} pièce${familleActive.nombre !== 1 ? 's' : ''}`}</span>
              {familleActive.aPartirDe && (
                <span className="inline-flex items-center gap-1.5"><Icon name="scale" size={16} /> dès {eurSansCentimes(familleActive.aPartirDe)}</span>
              )}
              <span className="inline-flex items-center gap-1.5"><Icon name="clock" size={16} /> découpe sous 24 h</span>
            </div>
          </div>
          <div className="pointer-events-none absolute -right-10 -top-16 hidden h-64 w-64 rounded-full bg-brass-wash sm:block" aria-hidden="true" />
        </header>
      ) : (
        <header className="py-2">
          <p className="eyebrow">{q ? 'Résultats de recherche' : 'Le comptoir'}</p>
          <h1 className="mt-2 text-[clamp(2rem,4.4vw,3.1rem)]">
            {q ? `« ${q} »` : 'Tout ce que nous découpons'}
          </h1>
          <p className="mt-2 max-w-2xl text-[0.9375rem] leading-relaxed text-muted">
            Vingt pièces, dix rayons. Le prix au kilo est toujours affiché ; l’estimation au poids
            conseillé aussi. Filtrez par ce que vous voulez faire : griller, mijoter, rôtir.
          </p>
        </header>
      )}
      {/* --------------------------------------------------------- barre d'outils
          Trois états, un seul jeu de classes :

            · **au repos** (en haut de page) — le compte, la disposition, le tri
              sur une ligne ; la barre de filtres horizontale en dessous ; les
              puces des filtres actifs en dernier ;
            · **collée** (`collee`, dès que la barre touche l'en-tête) — une
              seule rangée de 56 px : soit les puces (téléphone), soit les
              gâchettes de filtre (portable), le tri à droite. La disposition et
              le « tout effacer » s'effacent ; ils ne servent pas en défilant ;
            · **large écran** (≥ 1400 px, 12,1") — les filtres sont dans la
              colonne de gauche ; la barre collante ne garde que le compte et le
              tri, et la rangée de gâchettes n'existe pas.

          Le point de rupture est `ecran` (1400 px) et non `2xl` (1536 px) : un
          portable 13,3" de 1440 px a la place de porter une colonne de 248 px,
          et il la reçoit désormais. Voir `app.css`. */}

      {/* Jalon invisible : sa sortie sous l'en-tête est le signal de collage. */}
      <div ref={jalonRef} aria-hidden="true" className="h-px w-full" />

      <div ref={zoneRef} data-zone-filtres data-collee={collee ? 'oui' : 'non'}
        className="pointer-events-none sticky top-16 z-30 -mx-5 mt-6 sm:-mx-6 lg:top-[120px]"
        /* La barre rétrécit, sa place dans le flux ne bouge pas : sans cette
           hauteur figée, chaque passage du point d'ancrage décalerait la grille
           de 80 px sous le curseur. */
        style={collee && hRepos ? { height: `${hRepos}px` } : undefined}>

        <div ref={barreRef}
          className={cn('pointer-events-auto bg-paper/95 px-5 backdrop-blur sm:px-6',
            collee ? 'absolute inset-x-0 top-0 py-1.5' : 'py-3')}>

          {/* Une seule rangée en mode collé, trois au repos : l'ordre visuel est
              piloté par `order`, pas par la position dans le document — le
              balisage reste unique, donc rien n'est démonté pendant le
              défilement (pas de perte de focus, pas de remontage des menus). */}
          <div className={cn('flex items-center', collee ? 'gap-2' : 'flex-wrap gap-3')}>
            {/* Pas de gâchette s'il n'y a rien à filtrer : ouvrir une feuille
                qui n'explique que son propre vide est un piège, pas une option.
                Trois familles sont dans ce cas (veau, tripes, épicerie) — elles
                comptent trop peu de pièces pour qu'une facette les distingue. */}
            {groupes.length > 0 && (
              <Bouton variante="quiet" taille="sm" icone="filter" onClick={() => setFiltresOuverts(true)}
                className="order-1 shrink-0 lg:hidden">
                Filtres{nbActifs > 0 ? ` · ${nbActifs}` : ''}
              </Bouton>
            )}

            {/* Deux textes pour un seul compte : celui qu'on lit, et celui
                qu'annonce un lecteur d'écran — qui doit rester dans le document
                même quand la version visible s'efface en mode compact. */}
            <p className={cn('order-2 text-sm text-muted', collee && 'hidden shrink-0 sm:block')} aria-hidden="true">
              {comptePieces}{familleActive ? ` · ${familleActive.nom}` : ''}
            </p>
            <span className="sr-only" aria-live="polite">
              {comptePieces}{familleActive ? ` · ${familleActive.nom}` : ''}
            </span>

            {/* Disposition et « tout effacer » : utiles au repos, encombrants en
                défilement. Le tri, lui, reste — c'est une action de lecture. */}
            <div className={cn('order-3 ml-auto flex items-center gap-2', collee && !large && 'hidden')}>
              {nbActifs > 0 && (
                <button type="button" onClick={toutEffacer}
                  className="hidden text-sm font-medium text-wine underline underline-offset-4 sm:block">
                  Tout effacer
                </button>
              )}

              {/* Disposition — deux vues, un seul composant de carte. */}
              <div className="hidden items-center gap-1 rounded-menu border border-lin-200 bg-surface p-1 sm:flex"
                role="group" aria-label="Disposition des cartes">
                {([
                  { valeur: 'grille', icone: 'grid' as const, libelle: 'Grille' },
                  { valeur: 'liste', icone: 'list' as const, libelle: 'Liste' },
                ]).map(v => (
                  <button key={v.valeur} type="button" aria-pressed={vue === v.valeur}
                    onClick={() => changerVue(v.valeur as 'grille' | 'liste')}
                    aria-label={`Vue ${v.libelle.toLowerCase()}`} title={`Vue ${v.libelle.toLowerCase()}`}
                    className={cn('grid h-10 w-10 place-items-center rounded-menu-item transition',
                      vue === v.valeur ? 'bg-ink text-lin-50 shadow-soft' : 'text-ink-soft hover:bg-linen hover:text-ink')}>
                    <Icon name={v.icone} size={17} />
                  </button>
                ))}
              </div>
            </div>

            <label htmlFor="tri" className="sr-only">Trier les résultats</label>
            <select id="tri" value={tri} onChange={e => setTri(e.target.value as Tri)}
              className={cn('shrink-0 rounded-menu border border-lin-200 bg-surface pl-3.5 pr-8 outline-none focus:border-wine',
                collee
                  ? 'order-6 ml-auto h-11 min-w-0 max-w-[62vw] flex-1 text-[0.8125rem] sm:w-[228px] sm:max-w-[228px] sm:flex-none'
                  : 'order-4 h-11 text-sm')}>
              {TRIS.map(t => <option key={t.valeur} value={t.valeur}>{t.libelle}</option>)}
            </select>

            {/* ----------------------------------------- gâchettes de filtres
                La rangée est stylée dans `app.css` (`.rangee-filtres`) et non
                par des utilitaires de point de rupture : Tailwind émet `ecran`
                **avant** `lg`, si bien qu'un `ecran:hidden` perdait contre le
                `lg:flex` du même élément — la barre restait affichée à 1440 px
                alors que la colonne avait repris le relais.
                En mode collé, la rangée devient un rail qui défile au doigt :
                elle ne passe plus sur trois lignes — c'était 191 px de haut à
                1024 px, un tiers de l'écran pour sept boutons. */}
            {groupes.length > 0 && (
              <div className={cn('order-5 rangee-filtres', collee && 'rangee-filtres--rail')}>
                <>
                  <span className={cn('shrink-0 pr-1 text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-muted',
                    collee && 'hidden xl:inline')}>
                    Filtrer
                  </span>
                  {groupes.map(groupe => {
                    const choisis = selection[groupe.id] ?? [];
                    const ouvert = menu?.id === groupe.id;
                    return (
                      <div key={groupe.id} className="relative shrink-0">
                        <button type="button" className="filtre__gachette"
                          aria-expanded={ouvert}
                          aria-controls={ouvert ? `menu-${groupe.id}` : undefined}
                          onClick={e => setMenu(m => (m?.id === groupe.id ? null : { id: groupe.id, ancre: e.currentTarget }))}>
                          {groupe.titre}
                          {choisis.length > 0 && <span className="filtre__compte">{choisis.length}</span>}
                          <Icon name="chevronDown" size={15}
                            className={cn('transition-transform duration-200', ouvert && 'rotate-180')} />
                        </button>
                      </div>
                    );
                  })}
                  {nbActifs > 0 && (
                    <button type="button" onClick={toutEffacer}
                      className="ml-1 shrink-0 text-sm font-medium text-wine underline underline-offset-4">
                      Tout effacer
                    </button>
                  )}
                </>
              </div>
            )}

            {/* Puces des filtres actifs. Elles restent sous les yeux pendant le
                défilement — sauf au-delà de 1400 px, où la colonne de gauche
                montre la sélection cochée, et sauf en mode collé sur portable,
                où les gâchettes portent déjà leur compte. */}
            {nbActifs > 0 && (
              <div className={cn('order-5 rangee-puces', collee && 'rangee-puces--rail')}>
                {Object.entries(selection).flatMap(([groupeId, choix]) => {
                  const groupe = groupes.find(g => g.id === groupeId);
                  return choix.map(id => {
                    const option = groupe?.options.find(o => o.id === id);
                    return option ? (
                      <Puce key={`${groupeId}-${id}`} supprimable onClick={() => basculer(groupeId, id)}
                        libelleSuppression={`Retirer le filtre ${option.libelle}`}>
                        {option.libelle}
                      </Puce>
                    ) : null;
                  });
                })}
                <button type="button" onClick={toutEffacer}
                  className={cn('shrink-0 text-sm font-medium text-wine underline underline-offset-4',
                    !collee && 'sm:hidden')}>
                  Tout effacer
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Le panneau vit hors de la rangée : dans un rail qui défile, un
            panneau en position absolue serait rogné à la première gâchette. */}
        {menu && (
          <MenuGroupe id={`menu-${menu.id}`} groupe={groupes.find(g => g.id === menu.id)!}
            ancre={menu.ancre} selection={selection} basculer={basculer} />
        )}
      </div>

      {/* ------------------------------------------------------------- contenu */}
      <div className="mt-2 ecran:grid ecran:grid-cols-[248px_1fr] ecran:gap-10">
        <aside className="hidden ecran:block">
          {/* Le `top` se calcule : en-tête (122 px ici) + hauteur réelle de la
              barre collante (68 px au repos, 56 px collée) + 12 px d'air. Une
              valeur figée à 188 px laissait la colonne glisser sous la barre
              dès que celle-ci se resserrait. */}
          <div className="sticky overflow-y-auto pb-6 pr-1"
            style={{
              top: `calc(${hEntete || 122}px + ${hBarre || 68}px + 12px)`,
              maxHeight: `calc(100vh - ${(hEntete || 122) + (hBarre || 68) + 24}px)`,
            }}>
            <PanneauFiltres groupes={groupes} selection={selection} basculer={basculer} />
          </div>
        </aside>

        <section aria-label="Produits">
          {resultats.length === 0 ? (
            <Vide titre="Aucune pièce ne correspond"
              texte="Retirez un filtre, ou élargissez la recherche. Nous avons peut-être la pièce sous un autre nom — appelez le comptoir, il découpe."
              action={<Bouton variante="ink" onClick={toutEffacer}>Réinitialiser les filtres</Bouton>} />
          ) : (
            <div data-vue={vue}
              className={cn('grid',
                vue === 'liste'
                  ? 'grid-cols-1 gap-3'
                  : 'grid-cols-2 gap-x-4 gap-y-9 sm:gap-x-6 lg:grid-cols-4')}>
              {resultats.map((p, i) => <ProductCard key={p.id} produit={p} prioritaire={i < 4} />)}
            </div>
          )}

          {q && (
            <div className="mt-10 rounded-2xl bg-linen p-5 text-sm text-ink-soft">
              Vous cherchiez <strong>« {q} »</strong> ?{' '}
              <button type="button" onClick={() => setParams({})} className="font-semibold text-wine underline underline-offset-4">
                Voir tout le comptoir
              </button>
            </div>
          )}
        </section>
      </div>

      {/* ------------------------------------------------- feuille de filtres */}
      <Feuille ouverte={filtresOuverts} onFermer={() => setFiltresOuverts(false)} cote="bottom"
        titre="Filtrer" sousTitre={familleActive ? familleActive.nom : 'Tout le comptoir'}
        pied={
          <div className="flex items-center gap-3">
            <button type="button" onClick={toutEffacer} disabled={nbActifs === 0}
              className="shrink-0 text-sm font-medium text-wine underline underline-offset-4 disabled:opacity-40">
              Réinitialiser
            </button>
            <Bouton bloc onClick={() => setFiltresOuverts(false)}>
              Voir les {comptePieces}
            </Bouton>
          </div>
        }>
        <PanneauFiltres groupes={groupes} selection={selection} basculer={basculer} />
      </Feuille>
    </main>
  );
}

/* -------------------------------------------------------------------------- */
/**
 * LE PANNEAU D'UN GROUPE — hors de la rangée, posé sur sa gâchette.
 *
 * Pourquoi un portail : en mode collé, la rangée des filtres devient un rail
 * qui défile horizontalement (`overflow-x: auto`). Un panneau en position
 * absolue y serait rogné dès la deuxième gâchette — on n'en verrait que la
 * première colonne. Le panneau vit donc à la racine du document, en `position:
 * fixed`, et se recale sur le rectangle de son bouton.
 *
 * Où il s'ouvre : sous la gâchette par défaut ; au-dessus quand le bas de
 * l'écran est plus court que le haut (une gâchette près du pied de page sur un
 * téléphone en paysage). Sa hauteur est bornée à la place disponible et il
 * défile pour le reste — jamais de panneau qui dépasse.
 */
type PositionMenu = { left: number; largeur: number; haut?: number; bas?: number; maxH: number };

function MenuGroupe({ id, groupe, ancre, selection, basculer }: {
  id: string; groupe: GroupeFacette; ancre: HTMLElement;
  selection: Selection; basculer: (g: string, o: string) => void;
}) {
  const [pos, setPos] = useState<PositionMenu | null>(null);

  useLayoutEffect(() => {
    if (!ancre) return;
    const placer = () => {
      const r = ancre.getBoundingClientRect();
      const largeur = Math.min(300, window.innerWidth - 20);
      const left = Math.min(Math.max(10, r.left), Math.max(10, window.innerWidth - largeur - 10));
      const dessous = window.innerHeight - r.bottom - 16;
      const dessus = r.top - 16;
      const versLeBas = dessous >= 240 || dessous >= dessus;
      const disponible = Math.max(160, versLeBas ? dessous : dessus);
      setPos(versLeBas
        ? { left, largeur, haut: Math.round(r.bottom + 8), maxH: Math.min(440, Math.round(disponible)) }
        : { left, largeur, bas: Math.round(window.innerHeight - r.top + 8), maxH: Math.min(440, Math.round(disponible)) });
    };
    placer();
    window.addEventListener('resize', placer);
    /* En capture : le rail de filtres défile aussi, et le panneau doit le suivre. */
    window.addEventListener('scroll', placer, true);
    return () => {
      window.removeEventListener('resize', placer);
      window.removeEventListener('scroll', placer, true);
    };
  }, [ancre]);

  if (typeof document === 'undefined' || !pos) return null;

  return createPortal(
    <div id={id} data-menu-filtre role="group" aria-label={groupe.titre}
      className="fixed z-[70] overflow-y-auto overscroll-contain rounded-menu border border-lin-200 bg-surface p-2 shadow-float"
      style={{
        left: pos.left, width: pos.largeur, top: pos.haut, bottom: pos.bas, maxHeight: pos.maxH,
      }}>
      <p className="px-2 pb-1.5 pt-1 text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-muted">
        {groupe.titre}
      </p>
      <OptionsGroupe groupe={groupe} selection={selection} basculer={basculer} compact />
    </div>,
    document.body,
  );
}

/* -------------------------------------------------------------------------- */
function PanneauFiltres({ groupes, selection, basculer }: {
  groupes: GroupeFacette[]; selection: Selection; basculer: (g: string, o: string) => void;
}) {
  if (!groupes.length) {
    return <p className="py-6 text-sm text-muted">Cette famille n’a pas assez de pièces pour être filtrée — elles sont toutes là.</p>;
  }
  return (
    <div>
      {groupes.map(groupe => (
        <fieldset key={groupe.id} className="border-b border-linen-deep/60 py-4 first:pt-0 last:border-0">
          <legend className="mb-2 text-sm font-semibold text-ink">{groupe.titre}</legend>
          <OptionsGroupe groupe={groupe} selection={selection} basculer={basculer} />
        </fieldset>
      ))}
    </div>
  );
}

/** Les cases d'un groupe — mêmes lignes dans la colonne et dans le panneau. */
function OptionsGroupe({ groupe, selection, basculer, compact }: {
  groupe: GroupeFacette; selection: Selection; basculer: (g: string, o: string) => void; compact?: boolean;
}) {
  return (
    <div className={compact ? 'space-y-0.5' : 'space-y-0.5'}>
      {groupe.options.map(o => {
        const coche = (selection[groupe.id] ?? []).includes(o.id);
        return (
          <label key={o.id}
            className={cn('flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-1 transition hover:bg-linen',
              compact && 'px-2', coche && 'text-ink')}>
            <input type="checkbox" checked={coche} onChange={() => basculer(groupe.id, o.id)}
              className="h-[18px] w-[18px] shrink-0 rounded accent-wine" />
            <span className="flex-1 text-[0.9375rem]">{o.libelle}</span>
            <span className="text-xs tabular-nums text-muted">{o.compte}</span>
          </label>
        );
      })}
    </div>
  );
}
